import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Skeleton from 'react-loading-skeleton';
import { useTranslation } from 'react-i18next';
import { FontWeight, Heading, LiliButton, LiliButtonType, Text } from '@lili/lili-frontend-common';
import ContentWrapper from '../../../generic-components/ContentWrapper';
import { useVendorData } from '../../../hooks/query/useVendorDataQuery';
import { ASSETS_BASE_URL } from '../../../constants/endpoints';
import { FEATURE_FLAG } from '../../../constants/general';
import { ActionModal } from '../../../DesignSystem/ActionModal';
import CopyButton from '../../../generic-components/CopyButton';
import { copyTextToClipboard } from '../../../helpers/copyClipboard.helper';
import { COPY_TIME, PROFILE_TEXTS } from '../../profile/constants';
import { APP_CENTER_TEXTS, EMPTY_VENDOR_DATA } from '../constants';
import { VIEWS_MAP } from '../../../router/constants';
import { IVendorData } from '../../../types';
import { trackMixPanel } from '../../../services/mixpanel.service';
import GustoApp from '../../../pages/AppCenter/pages/GustoApp';
import { validateFeatureFlag } from '../../../services/featureFlagService';
import { useStore } from '../../../store';
import { ReactComponent as CopyIcon } from '../../../assets/icons/svg/shared/ic-shared-copy.svg';
import { reportActOnSpeedBump, reportViewSpeedBump } from '../../../analytics/speedBump.analytics';
import PageLayout from '../../../generic-components/PageLayout/PageLayout';
import PageHeader from '../../../generic-components/PageHeader/PageHeader';

const AppNames = {
  GUSTO: '15',
};

const Instructions = () => {
  // hooks
  const { vendorId } = useParams();
  const query = vendorId ? +vendorId : 0;
  const { customerId } = useStore((state) => state.user);
  const { refetch, isRefetching, isLoading } = useVendorData(customerId, query);
  const { t } = useTranslation();

  // get data
  const getVendorImage = (logoUrl: string) => `${ASSETS_BASE_URL}/unified/appcenter/${logoUrl}@2x.png`;
  const { getPrimaryAccount } = useStore((state) => state);

  // states
  const [vendorData, setVendorData] = useState<IVendorData>(EMPTY_VENDOR_DATA);
  const [accountInfoShown, setAccountInfoShown] = useState<boolean>(false);
  const [routingNumberIsCopied, setRoutingNumberIsCopied] = useState<boolean>(false);
  const [accountNumberIsCopied, setAccountNumberIsCopied] = useState<boolean>(false);
  const [showLeaveWarning, setShowLeaveWarning] = useState<boolean>(false);

  useEffect(() => {
    refetch().then((res) => {
      setVendorData(res.data);
      trackMixPanel('Act on AppCenter Hub Screen', {
        Action: 'Tap App',
        App: res.data?.name,
      });
    });
  }, []);

  const handleCopyClick = (copyText: string, filed: string) => {
    copyTextToClipboard(copyText).then(() => {
      if (filed === APP_CENTER_TEXTS.ROUTING_NUMBER) {
        setRoutingNumberIsCopied(true);
      } else {
        setAccountNumberIsCopied(true);
      }
      setTimeout(() => {
        setRoutingNumberIsCopied(false);
        setAccountNumberIsCopied(false);
      }, COPY_TIME.COPY_TIMEOUT);
    });
  };
  const isGustoApp = vendorId === AppNames.GUSTO;
  const showNewGustoApp = validateFeatureFlag(FEATURE_FLAG.Gusto);

  const handleRedirectOutside = () => {
    reportActOnSpeedBump('Continue');
    const url = vendorData?.displayOptions?.[0].actions?.[0]?.url || '';
    window.open(url, '_blank');
    setShowLeaveWarning(false);
  };

  const handleCancelRedirectOutside = () => {
    reportActOnSpeedBump('Back');
    setShowLeaveWarning(false);
  };

  const handleShowSpeedBump = () => {
    setShowLeaveWarning(true);
    reportViewSpeedBump(`App Center - ${vendorData?.name}`);
  };

  return (
    <PageLayout>
      <PageHeader title={t('APP_CENTER_TITLE')} pathToNavigate={`/${VIEWS_MAP.APP_CENTER}`} />
      {isGustoApp && showNewGustoApp ? (
        <ContentWrapper>
          <GustoApp gustoData={vendorData} isLoading={isLoading || isRefetching} />
        </ContentWrapper>
      ) : (
        <div className='w-full h-full flex gap-6 place-content-start relative top-0 justify-center items-center min-h-[568px] bg-bw00 rounded-md border border-outline-primary-bw-03 shadow-widgetCard'>
          <div className='w-[500px]'>
            {isRefetching || isLoading ? (
              <div className='w-full h-full'>
                <Skeleton className='my-3' count={9} />
              </div>
            ) : vendorData && vendorData.displayOptions.length > 0 ? (
              <div className='h-full flex flex-col items-center gap-[16px] w-[500px]'>
                <img src={getVendorImage(vendorData.logoUrl)} alt={vendorData.logoUrl} className='h-[40px] w-[40px]' />
                <Heading.H8 fontWeight={FontWeight.SEMIBOLD} className='text-center'>
                  {vendorData.displayOptions[0].title}
                </Heading.H8>
                <Text.H10 className='text-text-main-secondary text-center '>
                  {vendorData.displayOptions[0].vendorDescription}
                </Text.H10>
                <ul className='p-[24px] border border-outline-primary-bw-03 rounded-[12px] flex flex-col gap-[12px] w-[500px]'>
                  {vendorData.displayOptions[0].steps.map((step: string, index: number) => (
                    <div key={step} className='flex flex-row gap-1'>
                      <Heading.H10 fontWeight={FontWeight.SEMIBOLD} className='text-text-brand-primary inline-block'>
                        {`${index + 1}.`}
                      </Heading.H10>
                      <Heading.H10>{step}</Heading.H10>
                    </div>
                  ))}
                </ul>
                {vendorData?.displayOptions[0]?.disclaimer && (
                  <Heading.H10 className='text-text-main-secondary'>
                    {vendorData.displayOptions[0].disclaimer}
                  </Heading.H10>
                )}
                <div className='mt-auto flex flex-col gap-4'>
                  <LiliButton
                    className='w-[17.5rem] h-[3.125rem] mt-[2.875rem]'
                    id={vendorData?.displayOptions[0].actions[0]?.text || 'Transfer'}
                    title={vendorData?.displayOptions[0].actions[0]?.text || 'Transfer'}
                    onClick={handleShowSpeedBump}
                  />
                  {showLeaveWarning && (
                    <ActionModal
                      title={t('NAVIGATE_OUTSIDE_MODAL.TITLE')}
                      description={t('NAVIGATE_OUTSIDE_MODAL.DESCRIPTION')}
                      onSubmit={handleRedirectOutside}
                      onCancel={handleCancelRedirectOutside}
                      cancelButtonTitle={t('NAVIGATE_OUTSIDE_MODAL.CANCEL')}
                      mainButtonTitle={t('NAVIGATE_OUTSIDE_MODAL.CONTINUE')}
                      disclaimerText={t('NAVIGATE_OUTSIDE_MODAL.DISCLAIMER')}
                    />
                  )}
                  {vendorData?.displayOptions[0]?.actions[1] && (
                    <LiliButton
                      type={LiliButtonType.TERTIARY}
                      className='w-[17.5rem]'
                      id={vendorData?.displayOptions[0].actions[1]?.text || ''}
                      title={vendorData?.displayOptions[0].actions[1]?.text || ''}
                      onClick={() => {
                        setAccountInfoShown(true);
                      }}
                    />
                  )}
                </div>
              </div>
            ) : null}
          </div>
          {accountInfoShown && vendorData?.displayOptions[0]?.actions[1] && (
            <div className='bg-bw00 w-[264px] h-[192px] px-8 p-6'>
              <h1 className='font-metricMedium text-bw08'>{t(APP_CENTER_TEXTS.BANK_DETAILS)}</h1>
              <div className='w-full border-b border-bw05 mt-3 mb-4' />
              <div className='flex flex-col gap-2'>
                <div className='w-full flex justify-between items-start'>
                  <div className='flex flex-col'>
                    <p className='text-[14px] text-action03 font-metricMedium'>{getPrimaryAccount().routingNumber}</p>
                    <p className='text-[14px] text-bw07'>{t(APP_CENTER_TEXTS.ROUTING_NUMBER)}</p>
                  </div>
                  <div className='flex items-center pt-1.5 justify-center relative'>
                    <span className='absolute bottom-3.5 text-xxs text-action03'>
                      {routingNumberIsCopied && PROFILE_TEXTS.COPIED}
                    </span>
                    <CopyButton
                      id='appCenterRoutingNumber'
                      className=''
                      childElement={
                        <CopyIcon className={`w-3 h-3 ${routingNumberIsCopied ? 'text-action03' : 'text-bw05'}`} />
                      }
                      handleOnClick={() => {
                        handleCopyClick(getPrimaryAccount().routingNumber, APP_CENTER_TEXTS.ROUTING_NUMBER);
                      }}
                    />
                  </div>
                </div>

                <div className='flex justify-between items-start'>
                  <div className='flex flex-col'>
                    <p className='text-[14px] text-action03 font-metricMedium'>
                      {getPrimaryAccount().achAccountNumber}
                    </p>
                    <p className='text-[14px] text-bw07'>{t(APP_CENTER_TEXTS.ACCOUNT_NUMBER)}</p>
                  </div>
                  <div className='flex items-center pt-1.5 justify-center relative'>
                    <span className='absolute bottom-3.5 text-xxs text-action03'>
                      {accountNumberIsCopied && PROFILE_TEXTS.COPIED}
                    </span>
                    <CopyButton
                      id='appCenterAchAccountNumber'
                      className=''
                      childElement={
                        <CopyIcon className={`w-3 h-3 ${accountNumberIsCopied ? 'text-action03' : 'text-bw05'}`} />
                      }
                      handleOnClick={() => {
                        handleCopyClick(getPrimaryAccount().achAccountNumber, APP_CENTER_TEXTS.ACCOUNT_NUMBER);
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </PageLayout>
  );
};

export default Instructions;
